// docs/186 EA12: a tall piece fades over what it hides. The pure part: which standing pieces draw over (and hide) a
// figure or a hovered square, found through a bucket grid so a frame looks only at the pieces near each target.
// A piece hides a target when it sorts in front of it (its foot is below the target's depth) and its opaque pixels
// cover enough of the target's rect. EncounterTerrain fades the pieces this names; nothing here touches Phaser.

/** A rect in board px. */
export interface Rect { x: number; y: number; w: number; h: number }

/** What a piece needs to be tested: its image rect and the depth it sorts at (Standing has both). */
export interface PieceBox extends Rect { foot: number }

/** Something that must show through: its drawn rect, its depth in the units layer and how much of it a piece must hide. */
export interface SeeTarget extends Rect { depth: number; cover: number }

/** The alpha a hiding piece fades to. */
export const FADE_ALPHA = 0.4;
/** How long a piece takes to fade out or back (ms). */
export const FADE_MS = 150;
/** A figure shows through a piece whose opaque pixels cover at least this much of its drawn rect. */
export const UNIT_COVER = 0.2;
/** A hovered square shows through a piece whose opaque pixels cover at least this much of it. */
export const SQUARE_COVER = 0.3;
/** An opaque pixel, for the cover count. */
const OPAQUE = 128;
/** The bucket side (px). */
const BUCKET = 96;

/** A figure's target: its drawn rect, sorted at `depth` (its feet). */
export const unitTarget = (r: Rect, depth: number): SeeTarget => ({ x: r.x, y: r.y, w: r.w, h: r.h, depth, cover: UNIT_COVER });

/** A square's target: the square, at the depth a figure standing on it sorts at (its lower edge, as `feetPoint`). */
export const squareTarget = (t: { x: number; y: number }, cell: number): SeeTarget => ({ x: t.x * cell, y: t.y * cell, w: cell, h: cell, depth: (t.y + 1) * cell, cover: SQUARE_COVER });

/**
 * A summed-area table of a piece's opaque pixels ((w+1)×(h+1)), mirrored when the piece is drawn flipped, so the cover
 * of any rect is four reads.
 */
export function opaqueSums(px: ArrayLike<number>, w: number, h: number, flipX = false): Uint32Array {
  const W = w + 1, out = new Uint32Array(W * (h + 1));
  for (let y = 0; y < h; y++) {
    let row = 0;
    for (let x = 0; x < w; x++) {
      const sx = flipX ? w - 1 - x : x;
      if (px[(y * w + sx) * 4 + 3]! >= OPAQUE) row++;
      out[(y + 1) * W + x + 1] = out[y * W + x + 1]! + row;
    }
  }
  return out;
}

/** Opaque pixels of a piece at `p` inside board rect `r`: from its sums, or its whole overlap without them. */
export function coveredPx(p: Rect, r: Rect, sums?: Uint32Array): number {
  const x0 = Math.max(0, Math.floor(r.x - p.x)), y0 = Math.max(0, Math.floor(r.y - p.y));
  const x1 = Math.min(p.w, Math.ceil(r.x + r.w - p.x)), y1 = Math.min(p.h, Math.ceil(r.y + r.h - p.y));
  if (x1 <= x0 || y1 <= y0) return 0;
  if (!sums) return (x1 - x0) * (y1 - y0);
  const W = p.w + 1;
  return sums[y1 * W + x1]! - sums[y0 * W + x1]! - sums[y1 * W + x0]! + sums[y0 * W + x0]!;
}

/** The pieces by the buckets their rects touch. */
export class PieceGrid {
  private readonly x0: number;
  private readonly y0: number;
  private readonly cols: number;
  private readonly rows: number;
  private readonly cells: number[][];
  private stamp: Uint32Array;
  private serial = 0;

  constructor(readonly pieces: readonly PieceBox[]) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of pieces) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x + p.w); y1 = Math.max(y1, p.y + p.h); }
    if (!pieces.length) { x0 = y0 = 0; x1 = y1 = 1; }
    this.x0 = Math.floor(x0); this.y0 = Math.floor(y0);
    this.cols = Math.max(1, Math.ceil((x1 - this.x0) / BUCKET)); this.rows = Math.max(1, Math.ceil((y1 - this.y0) / BUCKET));
    this.cells = Array.from({ length: this.cols * this.rows }, () => []);
    this.stamp = new Uint32Array(pieces.length);
    pieces.forEach((p, i) => this.each(p, (c) => this.cells[c]!.push(i)));
  }

  private each(r: Rect, f: (cell: number) => void): void {
    const bx0 = Math.max(0, Math.floor((r.x - this.x0) / BUCKET)), by0 = Math.max(0, Math.floor((r.y - this.y0) / BUCKET));
    const bx1 = Math.min(this.cols - 1, Math.floor((r.x + r.w - 1 - this.x0) / BUCKET)), by1 = Math.min(this.rows - 1, Math.floor((r.y + r.h - 1 - this.y0) / BUCKET));
    for (let y = by0; y <= by1; y++) for (let x = bx0; x <= bx1; x++) f(y * this.cols + x);
  }

  /** Every piece whose rect overlaps `r` (each once). */
  near(r: Rect): number[] {
    const out: number[] = [];
    if (++this.serial === 0xffffffff) { this.stamp.fill(0); this.serial = 1; }
    const s = this.serial;
    this.each(r, (c) => {
      for (const i of this.cells[c]!) {
        if (this.stamp[i] === s) continue;
        this.stamp[i] = s;
        const p = this.pieces[i]!;
        if (p.x < r.x + r.w && p.x + p.w > r.x && p.y < r.y + r.h && p.y + p.h > r.y) out.push(i);
      }
    });
    return out;
  }

  /**
   * The pieces that hide `t`: in front of it (foot below its depth) with enough of its rect under their opaque
   * pixels (`sums(i)`: piece i's `opaqueSums`, undefined to count its whole rect). Added to `out`.
   */
  hiding(t: SeeTarget, sums: (i: number) => Uint32Array | undefined, out: Set<number>): Set<number> {
    const area = t.w * t.h;
    if (area <= 0) return out;
    for (const i of this.near(t)) {
      const p = this.pieces[i]!;
      if (p.foot <= t.depth || out.has(i)) continue;
      if (coveredPx(p, t, sums(i)) >= t.cover * area) out.add(i);
    }
    return out;
  }
}

/** One step of a fade toward `to` over `dt` ms (a full fade takes FADE_MS). */
export function stepFade(alpha: number, to: number, dt: number): number {
  const step = ((1 - FADE_ALPHA) * dt) / FADE_MS;
  return alpha < to ? Math.min(to, alpha + step) : Math.max(to, alpha - step);
}
